import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _possibleConstructorReturn from "@babel/runtime/helpers/possibleConstructorReturn";
import _assertThisInitialized from "@babel/runtime/helpers/assertThisInitialized";
import _getPrototypeOf from "@babel/runtime/helpers/getPrototypeOf";
import _get from "@babel/runtime/helpers/get";
import _inherits from "@babel/runtime/helpers/inherits";
import * as THREE from 'three';
import GeometryLayer from "./GeometryLayer";
import OrientedImageMaterial from "../Renderer/OrientedImageMaterial";
import GeoJsonParser from "../Parser/GeoJsonParser";
import CameraCalibrationParser from "../Parser/CameraCalibrationParser";
import Coordinates from "../Core/Geographic/Coordinates";
import OrientationUtils from "../Utils/OrientationUtils";
var coord = new Coordinates('EPSG:4978', 0, 0, 0);

function updatePano(context, camera, layer) {
  // look for the closest oriented image
  var minD = Infinity;
  var minI = -1;

  for (var i = 0; i < layer.panos.length; i++) {
    var position = layer.panos[i].position;
    var D = camera.position.distanceTo(position);

    if (D < minD) {
      minD = D;
      minI = i;
    }
  } // detection of oriented image change


  var newPano = layer.panos[minI];

  if (newPano && layer.currentPano != newPano) {
    layer.currentPano = newPano; // callback to indicate current pano has changed

    layer.onPanoChanged({
      previousPanoPosition: layer.getPreviousPano() ? layer.getPreviousPano().position : undefined,
      currentPanoPosition: layer.getCurrentPano().position,
      nextPanoPosition: layer.getNextPano().position
    }); // prepare informations about the needed textures

    var imagesInfo = layer.cameras.map(function (cam) {
      return {
        cameraId: cam.name,
        panoId: newPano.id,
        toString: function toString() {
          var separator = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
          return "".concat(cam.name).concat(separator).concat(newPano.id);
        }
      };
    });
    var command = {
      layer: layer,
      // put informations about image URL as extent to be used by generic DataSourceProvider, OrientedImageSource will use that.
      extentsSource: imagesInfo,
      view: context.view,
      threejsLayer: layer.threejsLayer,
      requester: newPano
    }; // async call to scheduler to get textures

    context.scheduler.execute(command).then(function (textures) {
      return layer.material.setTextures(textures, newPano);
    });
  }
}

function updateBackground(layer) {
  if (layer.background && layer.currentPano) {
    layer.background.position.copy(layer.currentPano.position);
    layer.background.updateMatrixWorld();
    layer.background.material = layer.material || layer.background.material;
  }
}

function createBackground(radius) {
  if (!radius || radius <= 0) {
    return undefined;
  }

  var geometry = new THREE.SphereGeometry(radius, 32, 32);
  var material = new THREE.MeshPhongMaterial({
    color: 0x7777ff,
    side: THREE.DoubleSide,
    transparent: true,
    opacity: 0.5,
    wireframe: true
  });
  var sphere = new THREE.Mesh(geometry, material);
  sphere.visible = true;
  sphere.name = 'OrientedImageBackground';
  return sphere;
}
/**
 * @classdesc OrientedImageLayer loads oriented images, and project these textures on the scene.
 * It is design to create an immersive view. </br>
 * It loads a set of panoramic position and orientation,
 * a set of camera calibration file (it's the same set of camera for each panoramic),
 * and a set of texture (each image for each camera for each panoramic), all organised in an {@link OrientedImageSource}. </br>
 * It creates an {@link OrientedImageMaterial} used to do projective texture mapping on the scene.
 * @extends GeometryLayer
 */


var OrientedImageLayer =
/*#__PURE__*/
function (_GeometryLayer) {
  _inherits(OrientedImageLayer, _GeometryLayer);

  /**
   * @constructor
   * @param { string } id - The id of the layer, a unique name.
   * @param { Object } config - configuration of the layer
   * @param { number } config.backgroundDistance - Radius in meter of the sphere used as a background
   * @param { function } config.onPanoChanged - callback fired when current panoramic changes
   * @param { string } config.projection - projection of the view
   * @param { string } config.orientation - Json object, using GeoJSon format to represent points,
   * it's a set of panoramic position and orientation.
   * @param { string } config.calibrations - Json object, representing a set of camera. see [CameraCalibrationParser]{@link module:CameraCalibrationParser}
   * @param { OrientedImageSource } config.source - Source used to build url of texture for each oriented image,
   * a tecture is need for each camera, for each panoramic.
   */
  function OrientedImageLayer(id) {
    var _this;

    var config = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

    _classCallCheck(this, OrientedImageLayer);

    _this = _possibleConstructorReturn(this, _getPrototypeOf(OrientedImageLayer).call(this, id, new THREE.Group(), config));
    _this.background = config.background || createBackground(config.backgroundDistance);

    if (_this.background) {
      _this.object3d.add(_this.background);
    } // currentPano is the current point, means it's the closest from the camera


    _this.currentPano = undefined; // store a callback to fire event when current panoramic change

    _this.onPanoChanged = config.onPanoChanged || function () {}; // panos is an array of feature point, representing many panoramics.
    // for each point, there is a position and a quaternion attribute.


    var p1 = GeoJsonParser.parse(config.orientation, {
      mergeFeatures: false,
      crsOut: config.projection
    }).then(function (res) {
      _this.panos = res.features; // we need to know if we are in globe view, for orientation.

      var isGlobe = config.projection == 'EPSG:4978'; // add position and quaternion attributes from point feature

      var i = 0;
      var _iteratorNormalCompletion = true;
      var _didIteratorError = false;
      var _iteratorError = undefined;

      try {
        for (var _iterator = _this.panos[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
          var pano = _step.value;
          // set position
          coord.crs = pano.crs;
          coord.setFromArray(pano.vertices);
          pano.position = coord.toVector3(); // set quaternion

          pano.quaternion = OrientationUtils.quaternionFromAttitude(pano.geometry[0].properties, coord, isGlobe); // TODO clean DataSourceProvider, so that we don't have this hack to do

          pano.material = {};
          pano.id = pano.geometry[0].properties.id;
          pano.index = i++;
        }
      } catch (err) {
        _didIteratorError = true;
        _iteratorError = err;
      } finally {
        try {
          if (!_iteratorNormalCompletion && _iterator["return"] != null) {
            _iterator["return"]();
          }
        } finally {
          if (_didIteratorError) {
            throw _iteratorError;
          }
        }
      }
    }); // array of cameras, represent the projective texture configuration for each panoramic.

    var p2 = CameraCalibrationParser.parse(config.calibration, {}).then(function (c) {
      _this.cameras = c; // create the material

      _this.material = new OrientedImageMaterial(_this.cameras);
    }); // wait for the twos promises to tell layer is ready.

    _this.whenReady = Promise.all([p1, p2]).then(function () {
      _this.ready = true;
      return _assertThisInitialized(_this);
    });
    return _this;
  } // eslint-disable-next-line


  _createClass(OrientedImageLayer, [{
    key: "update",
    value: function update() {}
  }, {
    key: "preUpdate",
    value: function preUpdate(context) {
      this.material.updateUniforms(context.camera.camera3D);
      updatePano(context, context.camera.camera3D, this);
      updateBackground(this);
    }
  }, {
    key: "getNextPano",
    value: function getNextPano() {
      var index = (this.currentPano.index + 1) % this.panos.length;
      return this.panos[index];
    }
  }, {
    key: "getCurrentPano",
    value: function getCurrentPano() {
      return this.currentPano;
    }
  }, {
    key: "getPreviousPano",
    value: function getPreviousPano() {
      var index = (this.currentPano.index - 1) % this.panos.length;
      return this.panos[index];
    }
    /**
     * Delete background, but doesn't delete OrientedImageLayer.material. For the moment, this material visibility is set to false.
     * You need to replace OrientedImageLayer.material applied on each object, if you want to continue displaying them.
     * This issue (see #1018 {@link https://github.com/iTowns/itowns/issues/1018}) will be fixed when OrientedImageLayer will be a ColorLayer.
     */

  }, {
    key: "delete",
    value: function _delete() {
      _get(_getPrototypeOf(OrientedImageLayer.prototype), "delete", this).call(this);

      this.material.visible = false;
      console.warn('You need to replace OrientedImageLayer.material applied on each object. This issue will be fixed when OrientedImageLayer will be a ColorLayer. the material visibility is set to false. To follow issue see https://github.com/iTowns/itowns/issues/1018');
    }
  }]);

  return OrientedImageLayer;
}(GeometryLayer);

export default OrientedImageLayer;